import React from 'react';
import {
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonInput,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonModal,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonHeader,
  IonContent,
} from '@ionic/react';
import { closeOutline, qrCodeOutline, trashOutline } from 'ionicons/icons';
import type { ShareLevel } from '../../domain/DeviceShare';
import { useDeviceShareCard } from '../../application/use-cases/deviceShares/useDeviceShareCard';

const levelLabel: Record<ShareLevel, string> = { view: 'Solo ver', control: 'Ver y controlar' };

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

const DeviceShareCard: React.FC<{ deviceId: string }> = ({ deviceId }) => {
  const {
    shares,
    pendingQr,
    loading,
    error,
    email,
    setEmail,
    level,
    setLevel,
    busy,
    qrDataUrl,
    setQrDataUrl,
    qrExpiresAt,
    handleInvite,
    handleRevoke,
    handleGenerateQr,
  } = useDeviceShareCard(deviceId);

  return (
    <>
        {loading && (
          <div className="ion-text-center">
            <IonSpinner color="primary" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && shares.length === 0 && (
          <IonText color="medium">
            <p>Todavía no compartiste esta estación con nadie.</p>
          </IonText>
        )}

        {shares.map((share) => (
          <IonItem key={share.id} lines="none" style={{ '--padding-start': 0 } as React.CSSProperties}>
            <IonLabel>
              <h3>{share.userName}</h3>
              <p>
                {share.userEmail} · {levelLabel[share.level]}
              </p>
            </IonLabel>
            <button className="delete-icon-btn" slot="end" onClick={() => handleRevoke(share.id)} aria-label={`Quitar a ${share.userName}`}>
              <IonIcon icon={trashOutline} />
            </button>
          </IonItem>
        ))}

        <IonItem>
          <IonLabel position="stacked">Nivel de acceso</IonLabel>
          <IonSelect value={level} onIonChange={(e) => setLevel(e.detail.value)}>
            <IonSelectOption value="view">Solo ver</IonSelectOption>
            <IonSelectOption value="control">Ver y controlar</IonSelectOption>
          </IonSelect>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Invitar por email</IonLabel>
          <IonInput
            type="email"
            value={email}
            placeholder="email@ejemplo.com"
            onIonInput={(e) => setEmail(e.detail.value ?? '')}
          />
        </IonItem>
        <IonButton expand="block" fill="outline" disabled={busy || !email.trim()} onClick={handleInvite}>
          Compartir por email
        </IonButton>

        <IonButton expand="block" disabled={busy} onClick={handleGenerateQr} style={{ marginTop: 8 }}>
          <IonIcon icon={qrCodeOutline} slot="start" />
          Generar código QR
        </IonButton>

        {pendingQr && (
          <IonText color="medium">
            <p style={{ fontSize: 12.5 }}>
              Hay un código QR activo ({levelLabel[pendingQr.level]}) que vence a las{' '}
              {formatTime(pendingQr.expiresAt)}.
            </p>
          </IonText>
        )}

        <IonModal isOpen={!!qrDataUrl} onDidDismiss={() => setQrDataUrl(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Código para compartir</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setQrDataUrl(null)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding ion-text-center">
            {qrDataUrl && (
              <img src={qrDataUrl} alt="Código QR para compartir la estación" style={{ maxWidth: '100%' }} />
            )}
            <IonText color="medium">
              <p>
                Nivel: {levelLabel[level]}. {qrExpiresAt && `Vence a las ${formatTime(qrExpiresAt)}.`} Quien lo
                escanee necesita tener una cuenta de DomIoT.
              </p>
            </IonText>
          </IonContent>
        </IonModal>
    </>
  );
};

export default DeviceShareCard;
